// services/dashboard.ts
import api from "../api/api";

export interface DashboardStats {
  totalUsers: number;
  activeUsers: number;
  totalReports: number;
  reportsToday: number;
  systemHealth: string;
  serverUptime: string;
  storageUsed: number;   // in GB
  storageLimit: number;  // in GB
}

export interface ActivityItem {
  id: number;
  type: string;
  description: string;
  user: string;
  time: string;   // formatted timestamp
  status: "success" | "warning" | "error";
}

export interface AnalyticsOverview {
  totalUsers: number;
  activeUsers: number;
  totalReports: number;
  reportsToday: number;
  reportsYesterday: number;
  reportsThisMonth: number;
  reportsThisYear: number;
}

export interface UserGrowth {
  month: string; // e.g. "Jan"
  users: number;
}

export interface Topclients {
  name: string;
  reports: number;
}

export interface AnalyticsOverviewResponse {
  overview: AnalyticsOverview;
  userGrowth: UserGrowth[];
  topclients: Topclients[];
  realtimeData: {
    reportsToday: number;
  };
}

export interface UserStats {
  daily: number;
  weekly: number;
  monthly: number;
}

export interface PlanDistribution {
  plan: string;
  count: number;
}

export interface UserAnalyticsResponse {
  engagement: UserStats;
  planDistribution: PlanDistribution[];
}

export interface ReportStats {
  status: string;
  count: number;
}

export interface DailyReport {
  date: string;  // YYYY-MM-DD
  count: number;
}

export interface ReportAnalyticsResponse {
  reportStats: ReportStats[];
  dailyReports: DailyReport[];
}

export interface ExportedAnalytics {
  date: string;
  clients: string | null;
  user: string | null;
  report_no: number;
  status: string;
  file_name: string;
}

export interface InstitutionSector {
  id: number;
  name: string;
}

export interface InstitutionSubSector {
  id: number;
  institution_sector_id: number;
  name: string;
}

export interface SectorWithSubsectors extends InstitutionSector {
  subsectors: InstitutionSubSector[];
}

// Get dashboard statistics
export async function getDashboardStats(access_token: string): Promise<DashboardStats | null> {
  try {
    const res = await api.get<DashboardStats>("/api/v1/admin/dashboard/stats", {
      headers: { Authorization: `Bearer ${access_token}` },
    });
    return res.data;
  } catch (error: any) {
    console.error("Get dashboard stats error:", error.response?.data || error.message);
    return null;
  }
}

// Get recent activity
export async function getRecentActivity(
  limit: number = 5,
  access_token: string
): Promise<ActivityItem[] | null> {
  try {
    const res = await api.get<ActivityItem[]>("/api/v1/admin/dashboard/activity", {
      params: { limit },
      headers: { Authorization: `Bearer ${access_token}` },
    });
    return res.data;
  } catch (error: any) {
    console.error("Get recent activity error:", error.response?.data || error.message);
    return null;
  }
}

export async function handleGetInstitutionSectors(
  access_token: string
): Promise<InstitutionSector[]> {
  try {
    const response = await api.get<InstitutionSector[]>("/api/v1/lookup/sectors/", {
      headers: { Authorization: `Bearer ${access_token}` },
    });
    return response.data;
  } catch (error: any) {
    console.error("Error fetching institution sectors:", error);
    throw error;
  }
}

export async function handleGetSectorSubsectorTree(
  access_token: string
): Promise<SectorWithSubsectors[]> {
  try {
    const response = await api.get<SectorWithSubsectors[]>("/api/v1/lookup/sector-subsector-tree/", {
      headers: { Authorization: `Bearer ${access_token}` },
    });
    return response.data;
  } catch (error: any) {
    console.error("Error fetching sector-subsector tree:", error);
    throw error;
  }
}

export async function handleGetInstitutionSubSectors(
  sectorId: number,
  access_token: string
): Promise<InstitutionSubSector[]> {
  try {
    const response = await api.get<InstitutionSubSector[]>(
      `/api/v1/lookup/subsectors/`,
      {
        headers: { Authorization: `Bearer ${access_token}` },
        params: { sector: sectorId }
      }
    );
    return response.data;
  } catch (error: any) {
    console.error("Error fetching institution sub-sectors:", error);
    throw error;
  }
}
